/**
 * iter129 - App router with React.lazy code-splitting.
 *
 * Previously every one of 100+ pages was eagerly imported, producing a single
 * ~3-4 MB JS bundle that had to be parsed before /login could render. The
 * user's perf complaint ("the website/app and the login page have become a
 * bit slow") traces directly to that.
 *
 * Refactor:
 *   EAGER  (first-paint critical, ~50 KB):
 *     - Landing, LoginPage, RegisterPage
 *     - Layout chrome: Navbar/Footer ship per-page, but the global
 *       AccessibilityWidget, CookieBanner, Toaster, FounderPulse,
 *       Breadcrumbs, OrganizationSchema, FloatingBackButton stay eager.
 *     - Protected + ScrollToTop helpers + AuthContext.
 *
 *   LAZY  (split into per-route chunks):
 *     - All conditions, admin, educator, parent, marketplace, free-tools,
 *       legal, onboarding, blog, and resource pages.
 *
 * All routes are wrapped in a single <Suspense> with a lightweight loader
 * so the first paint shows a centred "Loading..." string instead of a blank
 * screen while a chunk fetches.
 */
import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";

import FounderPulse from "@/components/FounderPulse";
import OrganizationSchema from "@/components/OrganizationSchema";
/* iter136.2 #6 - Breadcrumbs import removed alongside the rendered trail. */
import FloatingBackButton from "@/components/FloatingBackButton";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import CookieBanner from "@/components/CookieBanner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// EAGER: first-paint critical pages.
// iter171 - Landing moved to lazy so /login and /register users no longer
// pay for LiveAIDemo, AnimatedAIStudioDemo, FoundersWall and
// ConditionExplorer just to reach the login form. Landing users see the
// same Suspense loader for a few hundred ms before their route mounts,
// but the main.js drops noticeably which is what the user is feeling as
// "the app has become slow".
import { LoginPage, RegisterPage } from "@/pages/Auth";

// Helper that wraps a named export inside a default for React.lazy.
const named = (factory, key) => lazy(() => factory().then((m) => ({ default: m[key] })));
const def = (factory) => lazy(factory);

const Landing = def(() => import("@/pages/Landing"));

// ----- LAZY: condition + marketing pages ------------------------------------
const ADHDPage             = def(() => import("@/pages/conditions/ADHDPage"));
const AutismPage           = def(() => import("@/pages/conditions/AutismPage"));
const DyslexiaPage         = def(() => import("@/pages/conditions/DyslexiaPage"));
const DownSyndromePage     = def(() => import("@/pages/conditions/DownSyndromePage"));
const SlowLearnersPage     = def(() => import("@/pages/conditions/SlowLearnersPage"));
// iter160 - New Conditions Expansion (July 2026)
const NF1Page              = def(() => import("@/pages/conditions/NF1Page"));
const DysgraphiaPage       = def(() => import("@/pages/conditions/DysgraphiaPage"));
const DyscalculiaPage      = def(() => import("@/pages/conditions/DyscalculiaPage"));
const DyspraxiaDCDPage     = def(() => import("@/pages/conditions/DyspraxiaDCDPage"));
const LogoPreviewPage      = def(() => import("@/pages/LogoPreviewPage"));
const DesignPreviewPage    = def(() => import("@/pages/DesignPreviewPage"));
const HeroPreviewA         = def(() => import("@/pages/preview/HeroPreview").then(m => ({ default: m.HeroPreviewA })));
const HeroPreviewB         = def(() => import("@/pages/preview/HeroPreview").then(m => ({ default: m.HeroPreviewB })));
const HeroPreviewC         = def(() => import("@/pages/preview/HeroPreview").then(m => ({ default: m.HeroPreviewC })));
const CoursesHubPage       = def(() => import("@/pages/CoursesPage"));
// iter178 - Deep Condition Pages (Improvement #5)
const ConditionsIndex      = def(() => import("@/pages/Conditions/ConditionsIndex"));
const ConditionPageShared  = def(() => import("@/pages/Conditions/ConditionPage"));
const StarterProfileQuiz   = def(() => import("@/pages/StarterProfileQuiz"));
const ScreenerPage         = def(() => import("@/pages/Screener/ScreenerPage"));
// iter187 - watermark-hint preview host (not linked from any nav)
const PreviewWatermarkHint = def(() => import("@/pages/preview/PreviewWatermarkHintPage"));
// iter188 - functional-IEP layout preview (Tamana-style India IEP)
const PreviewFunctionalIEP = def(() => import("@/pages/preview/PreviewFunctionalIEPPage"));
// iter188 - functional IEP editor (v1-v4 customization)
const FunctionalIEPEditor = def(() => import("@/pages/educator/FunctionalIEPEditorPage"));
// iter188.4 - admin clinical review of every functional IEP
const AdminFunctionalIEP = def(() => import("@/pages/admin/AdminFunctionalIEPPage"));
// iter176 - Old empty CPD "CoursesPage" (CoursesResources.jsx::CoursesPage)
// removed per user request. /courses now points to the new practitioner-led
// hub. ResourcesPage export is still used for /resources.
const AboutUsPage          = def(() => import("@/pages/AboutUsPage"));
const PartnersPage         = def(() => import("@/pages/PartnersPage"));
const GoogleReturn         = def(() => import("@/pages/GoogleReturn"));
const Pricing              = def(() => import("@/pages/Pricing"));
const SampleIEP            = def(() => import("@/pages/SampleIEP"));
const Methodology          = def(() => import("@/pages/Methodology"));
const Tools                = def(() => import("@/pages/Tools"));
const PeerReviewProgram    = def(() => import("@/pages/PeerReview"));
const FreeTools            = def(() => import("@/pages/FreeTools"));
const Science              = def(() => import("@/pages/Science"));
const AccessibilityStatement = def(() => import("@/pages/AccessibilityStatement"));
const Blog                 = def(() => import("@/pages/Blog"));
const BlogPost             = def(() => import("@/pages/BlogPost"));
const TryAIStudio          = def(() => import("@/pages/TryAIStudio"));
const ResourceLibrary      = def(() => import("@/pages/ResourceLibrary"));
const ResourceDetail       = def(() => import("@/pages/ResourceDetail"));
const FreeResourcesLibrary = def(() => import("@/pages/FreeResourcesLibrary"));
const FreeResourceDetail   = def(() => import("@/pages/FreeResourceDetail"));
const FluencyTrackerV2     = def(() => import("@/pages/freeResources/FluencyTracker"));
const SentenceStripsV2     = def(() => import("@/pages/freeResources/SentenceStrips"));
const WeeklyPlannerV2      = def(() => import("@/pages/freeResources/WeeklyPlanner"));
const GamesHubV2           = def(() => import("@/pages/freeResources/GamesHub"));
const GamePlay             = def(() => import("@/pages/GamePlay"));
const Unsubscribe          = def(() => import("@/pages/Unsubscribe"));
const Celebrate            = def(() => import("@/pages/Celebrate"));
const Marketplace          = def(() => import("@/pages/Marketplace"));
const CheckoutSuccess      = def(() => import("@/pages/CheckoutSuccess"));
const InvitePage           = def(() => import("@/pages/InvitePage"));
const AIStudioLanding      = def(() => import("@/pages/AIStudioLanding"));
const AIStudioTool         = def(() => import("@/pages/AIStudioTool"));
const ProgressReportGenerator = def(() => import("@/pages/ProgressReportGenerator"));
// iter155 - Public quality gate explainer
const QualityGatePage      = def(() => import("@/pages/QualityGatePage"));

// ----- LAZY: Phase 2 Platform Handover --------------------------------------
const SchoolDashboard      = def(() => import("@/pages/school/SchoolDashboard"));
const ComplianceDashboard  = def(() => import("@/pages/school/ComplianceDashboard"));
const TransitionPlanner    = def(() => import("@/pages/school/TransitionPlanner"));
const JoinSchool           = def(() => import("@/pages/school/JoinSchool"));
const IepGoalCoach         = def(() => import("@/pages/educator/IepGoalCoach"));
const ForSchools           = def(() => import("@/pages/ForSchools"));

// ----- LAZY: email-landing forms --------------------------------------------
const DashboardRedirect      = named(() => import("@/pages/EmailLandingPages"), "DashboardRedirect");
const VerifyEmailPage        = named(() => import("@/pages/EmailLandingPages"), "VerifyEmailPage");
const ResetPasswordPage      = named(() => import("@/pages/EmailLandingPages"), "ResetPasswordPage");
const AcceptInvitePage       = named(() => import("@/pages/EmailLandingPages"), "AcceptInvitePage");
const AccountBillingPage     = named(() => import("@/pages/EmailLandingPages"), "AccountBillingPage");
const AccountNotificationsPage = named(() => import("@/pages/EmailLandingPages"), "AccountNotificationsPage");
const AccountDeletePage      = named(() => import("@/pages/EmailLandingPages"), "AccountDeletePage");
const ForgotPasswordPage     = named(() => import("@/pages/EmailLandingPages"), "ForgotPasswordPage");

// ----- LAZY: onboarding + parent + learn ------------------------------------
const OnboardingStep1 = def(() => import("@/pages/onboarding/OnboardingStep1"));
const OnboardingStep2 = def(() => import("@/pages/onboarding/OnboardingStep2"));
const OnboardingStep3 = def(() => import("@/pages/onboarding/OnboardingStep3"));
const OnboardingStep4 = def(() => import("@/pages/onboarding/OnboardingStep4"));
const ParentDashboard = def(() => import("@/pages/parent/ParentDashboard"));
const MyGames         = def(() => import("@/pages/parent/MyGames"));
// iter210.5 - Therapist Dashboard (read-only clinical review surface)
const TherapistDashboard = def(() => import("@/pages/therapist/TherapistDashboard"));
const ChildProgress   = def(() => import("@/pages/parent/ChildProgress"));
const LearnHome       = def(() => import("@/pages/learn/LearnHome"));
const ActivityPlayer  = def(() => import("@/pages/learn/ActivityPlayer"));
const EmotionFaceMatchPage      = named(() => import("@/pages/learn/SpecActivityPages"), "EmotionFaceMatchPage");
const SocialStoryPlayerPage     = named(() => import("@/pages/learn/SpecActivityPages"), "SocialStoryPlayerPage");
const SupermarketSimulationPage = named(() => import("@/pages/learn/SpecActivityPages"), "SupermarketSimulationPage");
const IEPProgressTrackerPage    = named(() => import("@/pages/learn/SpecActivityPagesB"), "IEPProgressTrackerPage");
const VisualScheduleBuilderPage = named(() => import("@/pages/learn/SpecActivityPagesB"), "VisualScheduleBuilderPage");
const PhonicsBlendingPage       = named(() => import("@/pages/learn/SpecActivityPagesB"), "PhonicsBlendingPage");
const ParentProgressStoryPage   = named(() => import("@/pages/learn/SpecActivityPagesB"), "ParentProgressStoryPage");
const WordPictureMatchPage      = named(() => import("@/pages/learn/SpecActivityPagesB"), "WordPictureMatchPage");
const CategorySortPage          = named(() => import("@/pages/learn/SpecActivityPagesB"), "CategorySortPage");

// ----- LAZY: educator -------------------------------------------------------
const EducatorDashboard = def(() => import("@/pages/educator/EducatorDashboard"));
const AIStudio          = def(() => import("@/pages/educator/AIStudio"));
const StudentProfile    = def(() => import("@/pages/educator/StudentProfile"));
const TherapistInviteAccept = def(() => import("@/pages/auth/TherapistInviteAccept"));
const TherapistCoverageGaps = def(() => import("@/pages/admin/TherapistCoverageGaps"));
const ProgressDashboard = def(() => import("@/pages/educator/ProgressDashboard"));
const ABATracker        = def(() => import("@/pages/educator/ABATracker"));
const AILibrary         = def(() => import("@/pages/educator/AILibrary"));
const StudioTemplates   = def(() => import("@/pages/educator/StudioTemplates"));
const Availability      = def(() => import("@/pages/educator/Availability"));
const AACBuilder        = def(() => import("@/pages/educator/AACBuilder"));
const StudentProgressPage = def(() => import("@/pages/educator/StudentProgressPage"));
const WeeklyProgressLog = def(() => import("@/pages/educator/WeeklyProgressLog"));

// ----- LAZY: marketplace ----------------------------------------------------
const DownloadablesMarketplace = def(() => import("@/pages/marketplace/DownloadablesMarketplace"));
const EducatorMarketplace      = def(() => import("@/pages/marketplace/Marketplace"));
const DownloadableDetail       = def(() => import("@/pages/marketplace/DownloadableDetail"));
const DownloadableUpload       = def(() => import("@/pages/marketplace/DownloadableUpload"));
const DownloadableLibrary      = def(() => import("@/pages/marketplace/DownloadableLibrary"));
const BookEducator             = def(() => import("@/pages/marketplace/BookEducator"));
const BookingConfirmed         = def(() => import("@/pages/marketplace/BookingConfirmed"));
const BookSessionCal           = def(() => import("@/pages/marketplace/BookSessionCal"));
const MessagesPage             = def(() => import("@/pages/messages/MessagesPage"));

// ----- LAZY: admin ----------------------------------------------------------
const LMSConnect            = def(() => import("@/pages/admin/LMSConnect"));
const DigestAdmin           = def(() => import("@/pages/admin/DigestAdmin"));
const ToolOfWeekAdmin       = def(() => import("@/pages/admin/ToolOfWeekAdmin"));
const BlogAdmin             = def(() => import("@/pages/admin/BlogAdmin"));
const AdminFunnel           = def(() => import("@/pages/admin/AdminFunnel"));
const AdminStoryFunnel      = def(() => import("@/pages/admin/AdminStoryFunnel"));
const AdminContentEngine    = def(() => import("@/pages/admin/AdminContentEngine"));
const AdminAudit            = def(() => import("@/pages/admin/AdminAudit"));
const AdminInvitesPage      = def(() => import("@/pages/admin/AdminInvitesPage"));
const AdminApiHealth        = def(() => import("@/pages/admin/AdminApiHealth"));
const AdminOnboarding       = def(() => import("@/pages/admin/AdminOnboarding"));
const ClinicalReviewQueue   = def(() => import("@/pages/admin/ClinicalReviewQueue"));
const AdminDashboard        = def(() => import("@/pages/admin/AdminDashboard"));
const FoundingMemberCampaign = def(() => import("@/pages/admin/FoundingMemberCampaign"));
const AdminAIOutputMonitor  = def(() => import("@/pages/admin/AdminAIOutputMonitor"));
const AdminCalActivity      = def(() => import("@/pages/admin/AdminCalActivity"));
const AdminIntegrationsHealth = def(() => import("@/pages/admin/AdminIntegrationsHealth"));
const AdminImageTiers       = def(() => import("@/pages/admin/AdminImageTiers"));
const ImagePipelineStatus   = def(() => import("@/pages/admin/ImagePipelineStatus"));
const AdminUsers            = def(() => import("@/pages/admin/AdminUsers"));
const BetaInvitesAdmin      = def(() => import("@/pages/admin/BetaInvitesAdmin"));
const AdminContentAudit     = def(() => import("@/pages/admin/AdminContentAudit"));
const GoogleOAuthSetupGuide = def(() => import("@/pages/admin/GoogleOAuthSetupGuide"));
const AdminOutreach          = def(() => import("@/pages/admin/Outreach"));
// iter132 P3 - SpectraMind Studio Today admin analytics page
const AIStudioToday         = def(() => import("@/pages/admin/AIStudioToday"));
// iter154 - Compliance overview dashboard (COPPA + GDPR + e-sigs + IndexNow)
const AdminComplianceDashboard = def(() => import("@/pages/admin/AdminComplianceDashboard"));

// ----- LAZY: courses --------------------------------------------------------
const CoursesPage   = named(() => import("@/pages/CoursesResources"), "CoursesPage");  // Legacy (unused after iter176); kept only so imports don't error until a full cleanup
const ResourcesPage = named(() => import("@/pages/CoursesResources"), "ResourcesPage");
const CoursePlayer  = def(() => import("@/pages/courses/CoursePlayer"));

// ----- LAZY: legal ----------------------------------------------------------
const TermsPage          = def(() => import("@/pages/legal/TermsOfService"));
const PrivacyPage        = def(() => import("@/pages/legal/PrivacyPolicy"));
const FAQPage            = def(() => import("@/pages/FAQPage"));
const DPAPage            = def(() => import("@/pages/legal/DataProcessingAgreement"));
const SchoolLicensePage  = def(() => import("@/pages/legal/SchoolLicenseAgreement"));
const AITransparencyPage = def(() => import("@/pages/legal/AITransparencyPage"));
const CookiePolicyPage   = def(() => import("@/pages/legal/CookiePolicy"));
const AccessibilityStatementNew = def(() => import("@/pages/legal/AccessibilityStatement"));
const SubprocessorsPage  = named(() => import("@/pages/legal/LegalPages"), "SubprocessorsPage");
const AcceptableUsePage  = named(() => import("@/pages/legal/LegalPages"), "AcceptableUsePage");
const TrustPage          = named(() => import("@/pages/legal/LegalPages"), "TrustPage");
const DSRForm            = def(() => import("@/pages/legal/DSRForm"));
const DataRights         = def(() => import("@/pages/DataRights"));  // iter153 Action 14 - CPRA

// ----- LAZY: free tools (heavy long-tail) -----------------------------------
const VisualTimer        = def(() => import("@/pages/free/VisualTimer"));
const TokenBoard         = def(() => import("@/pages/free/TokenBoard"));
const BehaviorCounter    = def(() => import("@/pages/free/BehaviorCounter"));
const FirstThen          = def(() => import("@/pages/free/FirstThen"));
const ChoiceBoard        = def(() => import("@/pages/free/ChoiceBoard"));
const FeelingCheckIn     = def(() => import("@/pages/free/FeelingCheckIn"));
const CalmDownPlan       = def(() => import("@/pages/free/CalmDownPlan"));
const RewardMenu         = def(() => import("@/pages/free/RewardMenu"));
const VisualScheduleMaker = def(() => import("@/pages/free/VisualScheduleMaker"));
const BehaviorChart      = def(() => import("@/pages/free/BehaviorChart"));
const SocialStoryBuilder = def(() => import("@/pages/free/SocialStoryBuilder"));
const CommunicationBoard = def(() => import("@/pages/free/CommunicationBoard"));
const TokenEconomy       = def(() => import("@/pages/free/TokenEconomy"));
const SensoryDiet        = def(() => import("@/pages/free/SensoryDiet"));
const IepGoalBank        = def(() => import("@/pages/free/IepGoalBank"));
const MorningRoutine     = def(() => import("@/pages/free/MorningRoutine"));
const SupermarketSim     = def(() => import("@/pages/free/SupermarketSim"));
const RestaurantSim      = def(() => import("@/pages/free/RestaurantSim"));
const ClothingSim        = def(() => import("@/pages/free/ClothingSim"));
const MarketSim          = def(() => import("@/pages/free/MarketSim"));
const TransportSim       = def(() => import("@/pages/free/TransportSim"));
const PostBankSim        = def(() => import("@/pages/free/PostBankSim"));
const BudgetSimulator    = def(() => import("@/pages/free/BudgetSimulator"));
const MoneyGames         = def(() => import("@/pages/free/MoneyGames"));
const DailyLiving        = def(() => import("@/pages/free/DailyLiving"));
const LifeSkillsQuiz     = def(() => import("@/pages/free/LifeSkillsQuiz"));
const SocialStoriesLibrary = def(() => import("@/pages/free/SocialStoriesLibrary"));
const SocialStoryViewer  = def(() => import("@/pages/free/SocialStoryViewer"));

// ----- Helper components ----------------------------------------------------
function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-12 text-center text-[#7A827C]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  const isOnboardingPath = loc.pathname.startsWith("/onboarding/");
  if (
    !isOnboardingPath
    && user.role !== "admin"
    && user.onboarding_started_at
    && !user.onboarding_completed_at
  ) {
    const next = user.onboarding_step >= 3
      ? "/onboarding/step-4"
      : user.onboarding_step >= 2
        ? "/onboarding/step-3"
        : user.onboarding_step >= 1
          ? "/onboarding/step-2"
          : "/onboarding/step-1";
    return <Navigate to={next} replace />;
  }
  if (role && !(Array.isArray(role) ? role : [role]).includes(user.role)) {
    const dest = user.role === "admin"
      ? "/admin/dashboard"
      : user.role === "educator"
        ? "/educator"
        : user.role === "therapist"
          ? "/therapist/dashboard"
          : "/parent";
    return <Navigate to={dest} replace />;
  }
  // iter210.5 - Therapist role must never reach content-generation surfaces.
  // Any Protected route without an explicit `role` still allows educators
  // and parents through; therapists are corralled to their own dashboard.
  if (!role && user.role === "therapist" && !loc.pathname.startsWith("/therapist/")) {
    return <Navigate to="/therapist/dashboard" replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function RouteLoader() {
  return (
    <div data-testid="route-loader"
         className="flex items-center justify-center min-h-[40vh] text-[#7A827C]">
      Loading…
    </div>
  );
}

export default function App() {
  useEffect(() => {
    try { document.documentElement.setAttribute("lang", "en"); } catch { /* noop */ }
  }, []);
  return (
    <HelmetProvider>
      <AuthProvider>
        <I18nProvider>
          <AccessibilityProvider>
            <BrowserRouter>
            <a href="#main-content" className="a11y-skip-link" data-testid="skip-to-main">
              Skip to main content
            </a>
            <Toaster position="top-right" richColors closeButton />
            <AccessibilityWidget />
            <CookieBanner />
            <PWAInstallPrompt />
            <ScrollToTop />
            <FounderPulse />
            <OrganizationSchema />
            {/* iter136.2 #6 - global Breadcrumbs trail removed across all dashboards;
                the role-aware Navbar + per-page page titles already orient users. */}
            <main id="main-content" tabIndex={-1} style={{ outline: "none" }}>
              <ChunkErrorBoundary>
                <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  {/* iter178 - Deep Condition Pages. The dynamic route
                      below handles all 10 slugs via the shared template.
                      Legacy per-condition routes redirect to the new
                      slug so old bookmarks still work. */}
                  <Route path="/conditions" element={<ConditionsIndex />} />
                  <Route path="/conditions/slow-learners" element={<Navigate to="/conditions/slow-learner" replace />} />
                  <Route path="/conditions/dyspraxia-dcd" element={<Navigate to="/conditions/dyspraxia" replace />} />
                  <Route path="/conditions/:slug" element={<ConditionPageShared />} />
                  <Route path="/logo-preview" element={<LogoPreviewPage />} />
                  <Route path="/design-preview" element={<DesignPreviewPage />} />
                  <Route path="/courses-hub" element={<CoursesHubPage />} />
                  <Route path="/start" element={<ScreenerPage />} />
                  <Route path="/screener" element={<ScreenerPage />} />
                  <Route path="/preview/watermark-hint" element={<PreviewWatermarkHint />} />
                  <Route path="/preview/functional-iep" element={<PreviewFunctionalIEP />} />
                  <Route path="/preview/hero-a" element={<HeroPreviewA />} />
                  <Route path="/preview/hero-b" element={<HeroPreviewB />} />
                  <Route path="/preview/hero-c" element={<HeroPreviewC />} />
                  <Route path="/educator/functional-iep/:iepId" element={<Protected><FunctionalIEPEditor /></Protected>} />
                  <Route path="/admin/functional-iep" element={<Protected role="admin"><AdminFunctionalIEP /></Protected>} />
                  <Route path="/cpd-courses" element={<CoursesPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/auth/google/return" element={<GoogleReturn />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/signup" element={<Navigate to="/register" replace />} />
                  <Route path="/dashboard" element={<DashboardRedirect />} />
                  <Route path="/verify" element={<VerifyEmailPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/accept-invite" element={<AcceptInvitePage />} />
                  <Route path="/invite" element={<InvitePage />} />
                  <Route path="/account/billing" element={<AccountBillingPage />} />
                  <Route path="/account/notifications" element={<AccountNotificationsPage />} />
                  <Route path="/account/delete" element={<AccountDeletePage />} />
                  <Route path="/account" element={<Navigate to="/account/notifications" replace />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/sample-iep" element={<SampleIEP />} />
                  <Route path="/methodology" element={<Methodology />} />
                  <Route path="/quality-gate" element={<QualityGatePage />} />
                  <Route path="/tools" element={<Tools />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/peer-review-program" element={<PeerReviewProgram />} />
                  <Route path="/free-tools" element={<FreeTools />} />
                  <Route path="/science" element={<Science />} />
                  <Route path="/free/visual-timer" element={<VisualTimer />} />
                  <Route path="/free/token-board" element={<TokenBoard />} />
                  <Route path="/free/behavior-counter" element={<BehaviorCounter />} />
                  <Route path="/free/first-then" element={<FirstThen />} />
                  <Route path="/free/choice-board" element={<ChoiceBoard />} />
                  <Route path="/free/feeling-checkin" element={<FeelingCheckIn />} />
                  <Route path="/free/calm-down" element={<CalmDownPlan />} />
                  <Route path="/free/reward-menu" element={<RewardMenu />} />
                  <Route path="/free/visual-schedule-maker" element={<VisualScheduleMaker />} />
                  <Route path="/free/behavior-chart" element={<BehaviorChart />} />
                  <Route path="/free/social-story-builder" element={<SocialStoryBuilder />} />
                  <Route path="/free/communication-board" element={<CommunicationBoard />} />
                  <Route path="/free/token-economy" element={<TokenEconomy />} />
                  <Route path="/free/sensory-diet" element={<SensoryDiet />} />
                  <Route path="/free/iep-goal-bank" element={<IepGoalBank />} />
                  <Route path="/free/morning-routine" element={<MorningRoutine />} />
                  <Route path="/free/supermarket-sim" element={<SupermarketSim />} />
                  <Route path="/free/restaurant-sim" element={<RestaurantSim />} />
                  <Route path="/free/clothing-sim" element={<ClothingSim />} />
                  <Route path="/free/market-sim" element={<MarketSim />} />
                  <Route path="/free/transport-sim" element={<TransportSim />} />
                  <Route path="/free/postbank-sim" element={<PostBankSim />} />
                  <Route path="/free/budget-simulator" element={<BudgetSimulator />} />
                  <Route path="/free/money-games" element={<MoneyGames />} />
                  <Route path="/free/daily-living" element={<DailyLiving />} />
                  <Route path="/free/life-skills-quiz" element={<LifeSkillsQuiz />} />
                  <Route path="/free/social-stories" element={<SocialStoriesLibrary />} />
                  <Route path="/free/social-stories/:storyId" element={<SocialStoryViewer />} />
                  <Route path="/try-ai-studio" element={<TryAIStudio />} />
                  <Route path="/studio" element={<Protected><AIStudioLanding /></Protected>} />
                  <Route path="/studio/progress-report" element={<Protected><ProgressReportGenerator /></Protected>} />
                  <Route path="/studio/:tool" element={<Protected><AIStudioTool /></Protected>} />
                  <Route path="/resources" element={<Navigate to="/free-resources" replace />} />
                  <Route path="/resources/play/:gameId" element={<GamePlay />} />
                  <Route path="/resources/:resourceId" element={<ResourceDetail />} />
                  <Route path="/free-resources" element={<FreeResourcesLibrary />} />
                  <Route path="/free-resources/fluency-tracker" element={<FluencyTrackerV2 />} />
                  <Route path="/free-resources/sentence-strips" element={<SentenceStripsV2 />} />
                  <Route path="/free-resources/weekly-planner" element={<WeeklyPlannerV2 />} />
                  <Route path="/free-resources/games" element={<GamesHubV2 />} />
                  <Route path="/free-resources/:slug" element={<FreeResourceDetail />} />
                  <Route path="/free/fluency-tracker" element={<Navigate to="/free-resources/fluency-tracker" replace />} />
                  <Route path="/free/sentence-strips" element={<Navigate to="/free-resources/sentence-strips" replace />} />
                  <Route path="/free/weekly-planner" element={<Navigate to="/free-resources/weekly-planner" replace />} />
                  <Route path="/free/games" element={<Navigate to="/free-resources/games" replace />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/celebrate/:childId" element={<Celebrate />} />
                  <Route path="/courses" element={<CoursesHubPage />} />
                  <Route path="/courses/:courseId" element={<Protected><CoursePlayer /></Protected>} />
                  <Route path="/marketplace" element={<BookSessionCal />} />
                  <Route path="/marketplace/educators" element={<EducatorMarketplace />} />
                  <Route path="/marketplace/booked" element={<Protected><BookingConfirmed /></Protected>} />
                  <Route path="/marketplace/:educatorId" element={<Protected><BookEducator /></Protected>} />
                  <Route path="/checkout/success" element={<Protected><CheckoutSuccess /></Protected>} />
                  <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
                  <Route path="/parent" element={<Protected role="parent"><ParentDashboard /></Protected>} />
                  <Route path="/parent/dashboard" element={<Protected role="parent"><ParentDashboard /></Protected>} />
                  <Route path="/parent/games" element={<Protected role="parent"><MyGames /></Protected>} />
                  <Route path="/therapist/dashboard" element={<Protected role="therapist"><TherapistDashboard /></Protected>} />
                  <Route path="/parent/child/:childId" element={<Protected role="parent"><ChildProgress /></Protected>} />
                  <Route path="/learn/:childId" element={<Protected><LearnHome /></Protected>} />
                  <Route path="/learn/:childId/play/:activityId" element={<Protected><ActivityPlayer /></Protected>} />
                  <Route path="/learn/:childId/spec/emotion-match" element={<Protected><EmotionFaceMatchPage /></Protected>} />
                  <Route path="/learn/:childId/spec/social-story" element={<Protected><SocialStoryPlayerPage /></Protected>} />
                  <Route path="/learn/:childId/spec/supermarket" element={<Protected><SupermarketSimulationPage /></Protected>} />
                  <Route path="/learn/:childId/spec/phonics" element={<Protected><PhonicsBlendingPage /></Protected>} />
                  <Route path="/learn/:childId/spec/word-picture" element={<Protected><WordPictureMatchPage /></Protected>} />
                  <Route path="/learn/:childId/spec/category-sort" element={<Protected><CategorySortPage /></Protected>} />
                  <Route path="/educator/iep-tracker" element={<Protected><IEPProgressTrackerPage /></Protected>} />
                  <Route path="/educator/visual-schedule" element={<Protected><VisualScheduleBuilderPage /></Protected>} />
                  <Route path="/parent/this-week" element={<Protected><ParentProgressStoryPage /></Protected>} />
                  <Route path="/educator" element={<Protected role="educator"><EducatorDashboard /></Protected>} />
                  <Route path="/educator/studio" element={<Protected role="educator"><AIStudio /></Protected>} />
                  <Route path="/educator/progress" element={<Protected role="educator"><ProgressDashboard /></Protected>} />
                  <Route path="/educator/student/:studentId" element={<Protected role="educator"><StudentProfile /></Protected>} />
                  <Route path="/educator/iep" element={<Navigate to="/studio/iep-goal" replace />} />
                  <Route path="/educator/content" element={<Navigate to="/studio/social-story" replace />} />
                  <Route path="/educator/aba" element={<Protected role="educator"><ABATracker /></Protected>} />
                  <Route path="/educator/library" element={<Protected role={["educator", "admin"]}><AILibrary /></Protected>} />
                  <Route path="/studio/templates" element={<Protected role="educator"><StudioTemplates /></Protected>} />
                  <Route path="/educator/availability" element={<Protected role="educator"><Availability /></Protected>} />
                  <Route path="/admin/lms" element={<Protected><LMSConnect /></Protected>} />
                  <Route path="/integrations" element={<Protected><LMSConnect /></Protected>} />
                  <Route path="/admin/digest" element={<Protected><DigestAdmin /></Protected>} />
                  <Route path="/admin/tool-of-week" element={<Protected><ToolOfWeekAdmin /></Protected>} />
                  <Route path="/admin/blog" element={<Protected role="admin"><BlogAdmin /></Protected>} />
                  <Route path="/admin/funnel" element={<Protected><AdminFunnel /></Protected>} />
                  <Route path="/admin/story-funnel" element={<Protected><AdminStoryFunnel /></Protected>} />
                  <Route path="/admin/content-engine" element={<Protected><AdminContentEngine /></Protected>} />
                  <Route path="/admin/audit" element={<Protected role="admin"><AdminAudit /></Protected>} />
                  <Route path="/admin/invites" element={<Protected role="admin"><AdminInvitesPage /></Protected>} />
                  <Route path="/admin/api-health" element={<Protected role="admin"><AdminApiHealth /></Protected>} />
                  <Route path="/admin/clinical-review" element={<Protected role="admin"><ClinicalReviewQueue /></Protected>} />
                  <Route path="/admin/ai-output-monitor" element={<Protected role="admin"><AdminAIOutputMonitor /></Protected>} />
                  <Route path="/admin/cal-activity" element={<Protected role="admin"><AdminCalActivity /></Protected>} />
                  <Route path="/admin/integrations-health" element={<Protected role="admin"><AdminIntegrationsHealth /></Protected>} />
                  <Route path="/admin/founding-campaign" element={<Protected role="admin"><FoundingMemberCampaign /></Protected>} />
                  <Route path="/admin/image-tiers" element={<Protected role="admin"><AdminImageTiers /></Protected>} />
                  <Route path="/admin/image-pipeline" element={<Protected role="admin"><ImagePipelineStatus /></Protected>} />
                  <Route path="/admin/users" element={<Protected role="admin"><AdminUsers /></Protected>} />
                  <Route path="/admin/beta-invites" element={<Protected role="admin"><BetaInvitesAdmin /></Protected>} />
                  <Route path="/admin/content-audit" element={<Protected role="admin"><AdminContentAudit /></Protected>} />
                  <Route path="/admin/google-oauth-setup" element={<Protected role="admin"><GoogleOAuthSetupGuide /></Protected>} />
                  <Route path="/admin/outreach" element={<Protected role="admin"><AdminOutreach /></Protected>} />
                  <Route path="/library" element={<Marketplace />} />
                  <Route path="/admin/onboarding" element={<Protected role="admin"><AdminOnboarding /></Protected>} />
                  <Route path="/admin/dashboard" element={<Protected role="admin"><AdminDashboard /></Protected>} />
                  <Route path="/admin/ai-studio-today" element={<Protected role="admin"><AIStudioToday /></Protected>} />
                  <Route path="/admin/compliance" element={<Protected role="admin"><AdminComplianceDashboard /></Protected>} />
                  <Route path="/dpa" element={<DPAPage />} />
                  <Route path="/school-license-agreement" element={<SchoolLicensePage />} />
                  <Route path="/ai-transparency" element={<AITransparencyPage />} />
                  <Route path="/cookies" element={<CookiePolicyPage />} />
                  <Route path="/subprocessors" element={<SubprocessorsPage />} />
                  <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                  <Route path="/trust" element={<TrustPage />} />
                  <Route path="/privacy/dsr" element={<DSRForm />} />
                  <Route path="/data-rights" element={<DataRights />} />
                  <Route path="/educator/aac" element={<Protected role="educator"><AACBuilder /></Protected>} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/accessibility" element={<AccessibilityStatementNew />} />
                  <Route path="/onboarding/step-1" element={<Protected><OnboardingStep1 /></Protected>} />
                  <Route path="/onboarding/step-2" element={<Protected><OnboardingStep2 /></Protected>} />
                  <Route path="/onboarding/step-3" element={<Protected><OnboardingStep3 /></Protected>} />
                  <Route path="/onboarding/step-4" element={<Protected><OnboardingStep4 /></Protected>} />
                  <Route path="/educator/students/:studentId/progress" element={<Protected role="educator"><StudentProgressPage /></Protected>} />
                  <Route path="/educator/weekly-log" element={<Protected role="educator"><WeeklyProgressLog /></Protected>} />
                  <Route path="/invite/therapist" element={<TherapistInviteAccept />} />
                  <Route path="/admin/therapist-coverage" element={<Protected role="admin"><TherapistCoverageGaps /></Protected>} />
                  <Route path="/downloadables" element={<DownloadablesMarketplace />} />
                  <Route path="/downloadables/upload" element={<Protected><DownloadableUpload /></Protected>} />
                  <Route path="/downloadables/library" element={<Protected><DownloadableLibrary /></Protected>} />
                  <Route path="/downloadables/success" element={<Protected><DownloadableLibrary /></Protected>} />
                  <Route path="/downloadables/:id" element={<DownloadableDetail />} />
                  {/* Phase 2 Platform Handover (P2.1/P2.2/P2.3) */}
                  <Route path="/school/dashboard" element={<Protected role="educator"><SchoolDashboard /></Protected>} />
                  <Route path="/school/compliance" element={<Protected role="educator"><ComplianceDashboard /></Protected>} />
                  <Route path="/school/transition" element={<Protected role="educator"><TransitionPlanner /></Protected>} />
                  <Route path="/join-school" element={<JoinSchool />} />
                  <Route path="/educator/goal-coach" element={<Protected role="educator"><IepGoalCoach /></Protected>} />
                  <Route path="/for-schools" element={<ForSchools />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              </ChunkErrorBoundary>
            </main>
            <FloatingBackButton />
          </BrowserRouter>
        </AccessibilityProvider>
      </I18nProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

// Silence unused-var checks for components that are referenced via JSX above.
// (kept empty intentionally - all imports are referenced)
